import { datadogRum } from '@datadog/browser-rum';
import { arrayMoveImmutable } from 'array-move';
import i18next from 'i18next';
import toast from 'react-hot-toast';
import { DropResult } from 'react-smooth-dnd';

import ToastMessageBlock from '@/components/Toasts/SuccessToast';

import { queryClient } from '@/services';

import { setAdminState } from '../../state';

import {
  getCategoryByCountryCodeQueryKey,
  getCategoryById,
  IntlCategoriesDto,
  IntlCategoryEntity,
  saveCategories,
  saveCategory,
} from '@/_generated/api-clients/content';

/** Handles the drop event for the drag and drop containers. */
export const handleDrop = async (
  section: 'recommended' | 'categories',
  dropResult: DropResult,
  countryCode: string,
) => {
  const { removedIndex, addedIndex } = dropResult;

  if (
    removedIndex === null ||
    addedIndex === null ||
    removedIndex === addedIndex
  ) {
    return;
  }

  const queryKey = getCategoryByCountryCodeQueryKey({
    query: {
      isAdmin: true,
      country: countryCode,
    },
  });

  const previousCategories =
    queryClient.getQueryData<IntlCategoriesDto>(queryKey);
  if (!previousCategories) {
    return;
  }

  const { categories = [], featured: recommended = [] } = previousCategories;

  const updated = arrayMoveImmutable(
    section === 'recommended' ? recommended : categories,
    removedIndex,
    addedIndex,
  ).map((item, idx) => ({
    ...item,
    displayIndex: idx,
  }));

  queryClient.setQueryData<IntlCategoriesDto>(queryKey, {
    ...previousCategories,
    [section === 'recommended' ? 'featured' : 'categories']: updated,
  });

  await queryClient.cancelQueries({ queryKey });

  try {
    await saveCategories({
      body: updated,
      throwOnError: true,
    });
  } catch (error) {
    const err = error as Error;
    queryClient.setQueryData<IntlCategoriesDto>(queryKey, previousCategories);
    datadogRum.addError(err);
    toast.custom(toastObj => (
      <ToastMessageBlock id={toastObj.id} severity="error">
        {i18next.t('Admin.failedToUpdateCategories', {
          count: updated.length,
        })}
      </ToastMessageBlock>
    ));
  } finally {
    queryClient.invalidateQueries({ queryKey });
  }
};

export const toggleCategoryVisibility = async (
  index: number,
  isFeatured: boolean,
  countryCode: string,
) => {
  setAdminState('visibilityModalState', prev => ({ ...prev, open: false }));

  const queryKey = getCategoryByCountryCodeQueryKey({
    query: {
      isAdmin: true,
      country: countryCode,
    },
  });

  const previousCategories =
    queryClient.getQueryData<IntlCategoriesDto>(queryKey);

  const categories = structuredClone(previousCategories);

  if (
    categories?.categories === undefined ||
    categories.featured === undefined
  ) {
    return;
  }

  /**
   * First update query cache with the new enabled state.
   */

  const categoryToUpdate =
    categories[isFeatured ? 'featured' : 'categories']?.at(index);
  if (!categoryToUpdate?.id) {
    return;
  }

  categoryToUpdate.enabled = !categoryToUpdate.enabled;
  const targetCollection = categories[isFeatured ? 'featured' : 'categories'];
  if (!targetCollection) {
    return;
  }
  targetCollection[index] = categoryToUpdate;

  queryClient.setQueryData<IntlCategoriesDto>(queryKey, categories);

  await queryClient.cancelQueries({ queryKey });

  /**
   * Then fetch the category from the API and update the category with the new enabled state.
   */

  const categoryDTO = await getCategory(categoryToUpdate.id, countryCode);
  if (!categoryDTO) {
    handleError(
      new Error('Failed to fetch category.'),
      queryKey,
      previousCategories,
    );
    return;
  }

  categoryDTO.enabled = !categoryDTO.enabled;

  try {
    await saveCategory({
      body: categoryDTO,
      throwOnError: true,
    });
  } catch (error) {
    handleError(error, queryKey, previousCategories);
  } finally {
    queryClient.invalidateQueries({ queryKey });
  }
};

const handleError = (
  error: unknown,
  queryKey: unknown[],
  previousCategories?: IntlCategoriesDto,
  msg?: string,
) => {
  const err = error as Error;
  queryClient.setQueryData<IntlCategoriesDto>(queryKey, previousCategories);
  datadogRum.addError(err);
  toast.custom(toastObj => (
    <ToastMessageBlock id={toastObj.id} severity="error">
      {msg ??
        i18next.t('Admin.failedToUpdateCategories', {
          count: 1,
        })}
    </ToastMessageBlock>
  ));
};

const getCategory = async (id: string, countryCode: string) => {
  try {
    return (
      await getCategoryById({
        path: {
          id,
        },
        query: {
          isAdmin: true,
          country: countryCode,
        },
        throwOnError: true,
      })
    ).data;
  } catch (_) {
    return undefined;
  }
};

export const addCategory = async (
  req: IntlCategoryEntity,
  recommended: boolean,
  countryCode: string,
) => {
  const queryKey = getCategoryByCountryCodeQueryKey({
    query: {
      isAdmin: true,
      country: countryCode,
    },
  });

  const previousCategories =
    queryClient.getQueryData<IntlCategoriesDto>(queryKey);
  if (!previousCategories) {
    return;
  }

  const { categories = [], featured = [] } = previousCategories;

  const computedId = req.name?.en
    ? req.name.en.replaceAll(' ', '-').toLowerCase()
    : req.name?.es
      ? req.name.es.replaceAll(' ', '-').toLowerCase()
      : '';

  const updated = [
    ...(recommended ? featured : categories),
    {
      ...req,
      id: computedId,
    },
  ];

  queryClient.setQueryData<IntlCategoriesDto>(queryKey, {
    ...previousCategories,
    [recommended ? 'featured' : 'categories']: updated,
  });

  await queryClient.cancelQueries({ queryKey });

  try {
    await saveCategories({
      body: [req],
      throwOnError: true,
    });
  } catch (error) {
    handleError(
      error,
      queryKey,
      previousCategories,
      i18next.t('Admin.failedToAddCategory'),
    );
  } finally {
    queryClient.invalidateQueries({ queryKey });
  }
};
